import { Icons } from '../ui/Icons'

type VisibilityValue = 'public' | 'unlisted' | 'invite_only'
type AudienceValue = 'everyone' | 'denomination'

// Combined selection key. Audience-gating only applies to public events, so we
// surface it as a 4th mutually-exclusive option alongside the visibility modes.
type SelectionKey = VisibilityValue | 'denomination'

interface VisibilityPickerProps {
  visibility: VisibilityValue
  audience: AudienceValue
  // Host's denomination (slug, e.g. "seventh-day-adventist"). When set, a 4th
  // "denomination only" option is offered. When null/undefined, it is hidden.
  denomination?: string | null
  onChange: (next: { visibility: VisibilityValue; audience: AudienceValue }) => void
}

const formatDenomination = (slug: string) =>
  slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export function VisibilityPicker({ visibility, audience, denomination, onChange }: VisibilityPickerProps) {
  const gatingAvailable = Boolean(denomination) && denomination !== 'other'
  const denomLabel = denomination ? formatDenomination(denomination) : ''

  // Derived selection: a public + denomination event reads as the 4th option.
  const selectedKey: SelectionKey =
    visibility === 'public' && audience === 'denomination' && gatingAvailable
      ? 'denomination'
      : visibility

  const options: {
    key: SelectionKey
    label: string
    description: string
    Icon: React.ComponentType<{ size?: number }>
    next: { visibility: VisibilityValue; audience: AudienceValue }
  }[] = [
    {
      key: 'public',
      label: 'Public',
      description: 'Appears in search and on the map for everyone',
      Icon: Icons.globe,
      next: { visibility: 'public', audience: 'everyone' },
    },
    ...(gatingAvailable
      ? [
          {
            key: 'denomination' as const,
            label: `${denomLabel} only`,
            description: `Only shows in search for people who share your ${denomLabel} denomination`,
            Icon: Icons.church,
            next: { visibility: 'public' as const, audience: 'denomination' as const },
          },
        ]
      : []),
    {
      key: 'unlisted',
      label: 'Unlisted',
      description: 'Share a link — anyone with it can RSVP, no account needed',
      Icon: Icons.link,
      next: { visibility: 'unlisted', audience: 'everyone' },
    },
    {
      key: 'invite_only',
      label: 'Invite only',
      description: 'Invited members only — guests must have an account',
      Icon: Icons.lock,
      next: { visibility: 'invite_only', audience: 'everyone' },
    },
  ]

  return (
    <div className="flex flex-col gap-2">
      {options.map(({ key, label, description, Icon, next }) => {
        const selected = selectedKey === key
        return (
          <button
            key={key}
            type="button"
            onClick={() => onChange(next)}
            aria-pressed={selected}
            aria-label={label}
            className={`flex items-center gap-3 px-3.5 py-3 rounded-xl cursor-pointer text-left font-sans border-[1.5px] transition-colors ${selected ? 'bg-primary-soft border-primary' : 'bg-s2 border-line-soft hover:border-line'}`}
          >
            <div
              className={`w-[34px] h-[34px] rounded-[9px] shrink-0 flex items-center justify-center ${selected ? 'bg-primary text-white' : 'bg-line-soft text-muted'}`}
            >
              <Icon size={15} />
            </div>
            <div className="flex-1">
              <div className="text-[13.5px] font-semibold text-ink mb-[1px]">{label}</div>
              <div className="text-[12px] text-muted">{description}</div>
            </div>
            {selected && <Icons.check size={14} className="text-primary shrink-0" />}
          </button>
        )
      })}
      {selectedKey === 'invite_only' && (
        <div className="text-[12px] text-muted pl-0.5 mt-0.5">
          After publishing, invite people from the event's manage page.
        </div>
      )}
    </div>
  )
}
